/**
 * Time-slot generation — the single source of truth for "which times can a
 * guest actually pick".
 *
 * Extracted from BookingModal.js (where it was a closure over component state)
 * so the /book page produces byte-identical slots. The behaviour below is a
 * faithful port; the only structural change is that the two verbatim copies of
 * the blocked-time filter collapsed into `isSlotBlocked()`.
 *
 * Inputs are now explicit instead of captured from render scope:
 *   generateTimeSlots({ selectedDate, tourId, blockedTimes, now })
 */

import {
  CHRISTMAS_TOUR_IDS,
  isChristmasEarlyStartDate,
  ROCKEFELLER_TOUR_ID,
  variantsAtChristmasTime,
  toDateString,
  EVENING_TOUR_START_MINUTES,
  LAST_RETURN_HOUR,
} from './constants.js';

/** Minutes past midnight for the fixed Christmas evening departures. */
const CHRISTMAS_EVENING_SLOT_MINUTES = [
  19 * 60,      // 7:00 PM  (1140)
  19 * 60 + 50, // 7:50 PM  (1190)
  20 * 60 + 40, // 8:40 PM  (1240)
  21 * 60 + 30, // 9:30 PM  (1290)
  22 * 60 + 20, // 10:20 PM (1340)
  23 * 60 + 10, // 11:10 PM (1390)
];

const CHRISTMAS_LAST_DAYTIME_MINUTES = 16 * 60 + 45; // 4:45 PM (1005)

/**
 * Slots close 30 minutes before departure.
 * Exported so copy that quotes the cutoff (the FAQ) reads the real number
 * instead of a hand-typed one that can drift away from the calendar.
 */
export const BOOKING_BUFFER_MINUTES = 30;

/**
 * How New York's timezone should be written for a guest, on a given DATE.
 *
 * The offset must come from the tour date, never from today. New York is GMT-4
 * in summer (EDT) and GMT-5 in winter (EST), so a hardcoded "-4" is wrong for
 * roughly four months of the year — and someone browsing in October for a
 * December ride has to be told GMT-5, not the offset that happens to apply
 * while they are reading. That is exactly the misunderstanding this label
 * exists to prevent, so getting it wrong would be worse than saying nothing.
 *
 * @param {string|Date|null} date  the SELECTED tour date; null for a dateless hint
 * @returns {string} e.g. "Eastern Time (New York, GMT-4)"
 */
export function newYorkTimezoneLabel(date = null) {
  if (!date) return 'New York local time (Eastern Time)';
  const d = date instanceof Date ? date : new Date(`${String(date).split('T')[0]}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'New York local time (Eastern Time)';
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'shortOffset',
    })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName');
    // shortOffset yields "GMT-4" / "GMT-5".
    return part ? `Eastern Time (New York, ${part.value})` : 'Eastern Time (New York)';
  } catch (_) {
    // shortOffset is well supported, but never let a label break the page.
    return 'Eastern Time (New York)';
  }
}

/**
 * Current wall-clock in New York, regardless of the visitor's own timezone.
 * Intl handles EST/EDT automatically.
 */
export function getNewYorkNow(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
  const year = get('year');
  const month = get('month');
  const day = get('day');
  const hour = get('hour');
  const minute = get('minute');

  return {
    year,
    month,
    day,
    hour,
    minute,
    dateString: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    totalMinutes: hour * 60 + minute,
  };
}

/**
 * Add-ons the team gets ready ahead (champagne goes in the fridge the night
 * before) can be ordered only for a ride on a LATER New York calendar day than
 * today (owner 2026-10-10). A calendar day, not 24 hours: at 10 PM tonight a
 * guest may still order for 9 AM tomorrow; at any time today, not for a ride
 * today. addons.same_day = 1 lifts it (Chocolate Box, Tavern drop-off; the boss
 * ticks it in admin → Add-ons). Shared by /book, the modal, the admin manual
 * booking warning and booking-api, which refuses it for guests.
 */
export function addonNeedsDayBefore(addon, rideDate, now = new Date()) {
  const day = String(rideDate || '').slice(0, 10);
  return Boolean(day) && Number(addon?.same_day) !== 1 && day <= getNewYorkNow(now).dateString;
}

export const ADDON_DAY_BEFORE_NOTE = 'Must be reserved by the night before your ride.';
export const addonLeadNote = (hours) => `Must be booked at least ${hours} hour${hours === 1 ? '' : 's'} before your ride.`;

/**
 * booking_date ("2026-10-11") + booking_time ("7:00 PM" or "19:00") in New York, as epoch ms; null when either
 * is unreadable. New York is 4 (EDT) or 5 (EST) hours behind UTC; the offset is the one in force at the ride,
 * read twice so a DST-switch day settles.
 */
export function newYorkRideStart(rideDate, rideTime) {
  const d = String(rideDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const t = String(rideTime || '').trim().toUpperCase().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/);
  if (!d || !t) return null;
  const hour = t[3] ? (Number(t[1]) % 12) + (t[3] === 'PM' ? 12 : 0) : Number(t[1]);
  const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), hour, Number(t[2]));
  let utc = wall;
  for (let i = 0; i < 2; i += 1) {
    const ny = getNewYorkNow(new Date(utc));
    utc = wall + (utc - Date.UTC(ny.year, ny.month - 1, ny.day, ny.hour % 24, ny.minute));
  }
  return utc;
}

/**
 * Why this add-on can no longer be ordered for that ride ('' = it can). One rule for /book, the modal, the
 * admin warning, booking-api and the pay-later guard (owner 2026-10-10):
 *   - lead_hours > 0 (photographers, videographers: 24): the ride must start at least that many hours from now;
 *   - otherwise same_day != 1: the calendar-day rule above (champagne is chilled the night before);
 *   - same_day = 1: always.
 * A list from an older worker carries neither field: allowed here, the worker decides.
 */
export function addonLateNote(addon, rideDate, rideTime, now = new Date()) {
  const hours = Number(addon?.lead_hours) || 0;
  if (hours > 0) {
    // No time picked yet: judged at the day's last minute, so only a day where every departure is too soon greys it out.
    const start = newYorkRideStart(rideDate, rideTime || '23:59');
    return start !== null && start - now.getTime() < hours * 3600000 ? addonLeadNote(hours) : '';
  }
  return addon?.same_day != null && addonNeedsDayBefore(addon, rideDate, now) ? ADDON_DAY_BEFORE_NOTE : '';
}

/** "A, B must be reserved by the night before your ride. C must be booked at least 24 hours before your ride." */
export function lateAddonsText(addons, rideDate, rideTime, now = new Date()) {
  const byNote = new Map();
  for (const addon of addons) {
    const note = addonLateNote(addon, rideDate, rideTime, now);
    if (note) byNote.set(note, [...(byNote.get(note) || []), addon.name]);
  }
  return [...byNote].map(([note, names]) => `${names.join(', ')} ${note[0].toLowerCase()}${note.slice(1)}`).join(' ');
}

/** What fixes late add-ons: a later date; with an hours rule among them, a later departure that day may do too. */
export const laterChoice = (addons) => `choose a later date${addons.some((a) => Number(a?.lead_hours) > 0) ? ' or time' : ''}`;

/** Shown when a new date or time took add-ons out of the basket. */
export const dayBeforeRemovedNotice = (addons, rideDate, rideTime) =>
  `Removed from your order. ${lateAddonsText(addons, rideDate, rideTime)} To add ${addons.length === 1 ? 'it' : 'them'} back, ${laterChoice(addons)}.`;

/**
 * Does an availability block cover this slot?
 *
 * Dates are inclusive; time ranges include their start and exclude their end.
 */
export function blockCoversDate(block, dateString) {
  if (!block) return false;
  const key = (value) => String(value || '').slice(0, 10);
  return key(block.block_date) === dateString || Boolean(
    block.start_date && block.end_date &&
    key(block.start_date) <= dateString && key(block.end_date) >= dateString
  );
}

export function isSlotBlocked(blockedTimes, { slotMinutes, dateString, tourId }) {
  if (!blockedTimes || blockedTimes.length === 0) return false;

  return blockedTimes.some((block) => {
    if (!blockCoversDate(block, dateString)) return false;

    const blockApplies =
      block.block_type === 'global' ||
      (block.tour_id != null && Number(block.tour_id) === Number(tourId));
    if (!blockApplies) return false;

    if (Number(block.is_all_day) === 1) return true;

    if (block.start_time && block.end_time) {
      const [startHour, startMin] = block.start_time.split(':').map(Number);
      const [endHour, endMin] = block.end_time.split(':').map(Number);
      const blockStart = startHour * 60 + (startMin || 0);
      const blockEnd = endHour * 60 + (endMin || 0);
      // Start inclusive, end exclusive.
      return slotMinutes >= blockStart && slotMinutes < blockEnd;
    }

    return false;
  });
}

/**
 * Operating window for a given tour + date.
 *   - Christmas / Holiday VIP: weekend + listed holidays start 8:30, else 10:00.
 *   - Rockefeller Royal Lights: night-only, 8:00 PM onward.
 *   - Everything else: weekend 8:00, weekday 10:00.
 */
export function getTourSchedule(tourId, selectedDate) {
  const dayOfWeek = selectedDate.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const dateString = toDateString(selectedDate);

  const isChristmasTour = CHRISTMAS_TOUR_IDS.includes(tourId);
  const isRockefellerNightTour = tourId === ROCKEFELLER_TOUR_ID;

  if (isChristmasTour) {
    const earlyStart = isWeekend || isChristmasEarlyStartDate(dateString);
    return {
      kind: 'christmas',
      startHour: earlyStart ? 8 : 10,
      startMinute: earlyStart ? 30 : 0,
      endHour: 23,
      slotInterval: 45,
    };
  }

  if (isRockefellerNightTour) {
    return { kind: 'standard', startHour: 20, startMinute: 0, endHour: 23, slotInterval: 30 };
  }

  const eveningStart = EVENING_TOUR_START_MINUTES[tourId];
  if (eveningStart != null) {
    // earliestMinutes is what actually gates the slots. The standard branch of
    // generateTimeSlots builds candidates hour by hour and ignores startMinute,
    // so a half-past opening has to be enforced by filtering, not by the loop
    // bounds — otherwise an 8:30 PM tour also offers 8:00 PM.
    return {
      kind: 'standard',
      startHour: Math.floor(eveningStart / 60),
      startMinute: eveningStart % 60,
      earliestMinutes: eveningStart,
      endHour: 23,
      slotInterval: 30,
    };
  }

  return {
    kind: 'standard',
    startHour: isWeekend ? 8 : 10,
    startMinute: 0,
    endHour: 23,
    slotInterval: 30,
  };
}

/**
 * "125 minutes" / "55 minutes" / 65 → minutes, or null when unreadable.
 * tours.duration is a free-text column, so this has to be forgiving.
 */
export function parseDurationMinutes(duration) {
  if (duration == null) return null;
  if (typeof duration === 'number') return Number.isFinite(duration) ? duration : null;
  const m = String(duration).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * The latest a tour may set off and still be back by LAST_RETURN_HOUR.
 *
 * Without this every tour was offered until 23:30 regardless of length, so a
 * 125-minute ride booked at 23:30 would put a carriage on the street until
 * 01:35 and write a driver-calendar entry to match. Derived from the duration
 * so it stays right for tours nobody has created yet.
 */
export function lastDepartureMinutes(duration) {
  const mins = parseDurationMinutes(duration);
  const close = LAST_RETURN_HOUR * 60 + 59;
  if (!mins) return close;
  return close - mins;
}

/**
 * Build the bookable slot list.
 *
 * @param {Date|null} selectedDate  Day being booked (local Date).
 * @param {number}    tourId        Numeric tour id (drives the schedule).
 * @param {Array}     blockedTimes  Availability blocks from the API.
 * @param {Date}      now           Injectable clock (tests / SSR).
 * @param {boolean}   restrictChristmasVariant
 *        Christmas variants do not all run at every departure — 8:30 AM is Park
 *        Only, 7:00 PM adds Park + Avenue, 8:40 PM adds Avenue Only. Two flows
 *        need opposite things here:
 *          - BookingModal picks the TIME first and then switches the guest to a
 *            variant that runs then, so it wants every Christmas slot (default).
 *          - /book picks the TOUR first, so it must hide the times that tour does
 *            not run — otherwise the guest books a departure that does not exist.
 *        Opt in with `true`; leaving it off preserves the modal's behaviour.
 * @returns {Array<{time:string, value:string, hour:number, minute:number}>}
 *          Unavailable slots are omitted entirely (not flagged) — same as before.
 */
export function generateTimeSlots({
  selectedDate,
  tourId,
  blockedTimes = [],
  now = new Date(),
  restrictChristmasVariant = false,
  // Free text, e.g. "125 minutes". Optional: when it is absent the behaviour is
  // exactly as before, so any caller that has not been updated is unaffected.
  duration = null,
} = {}) {
  if (!selectedDate) return [];

  const currentTourId = parseInt(tourId, 10);
  const dateString = toDateString(selectedDate);
  const schedule = getTourSchedule(currentTourId, selectedDate);

  const nyNow = getNewYorkNow(now);
  const isToday = dateString === nyNow.dateString;
  const cutoffMinutes = nyNow.totalMinutes + BOOKING_BUFFER_MINUTES;

  // Candidate departure times, in minutes past midnight.
  const candidateMinutes = [];

  if (schedule.kind === 'christmas') {
    // Two distinct sessions: 8:30 AM → 4:45 PM every 45 min, then a fixed
    // evening list. The 5:00–6:59 PM gap is intentional (drivers reset).
    const startMinutes = schedule.startHour * 60 + schedule.startMinute;
    for (let m = startMinutes; m <= CHRISTMAS_LAST_DAYTIME_MINUTES; m += schedule.slotInterval) {
      candidateMinutes.push(m);
    }
    candidateMinutes.push(...CHRISTMAS_EVENING_SLOT_MINUTES);
  } else {
    // Standard tours: on the hour and half-hour through the last hour.
    for (let hour = schedule.startHour; hour <= schedule.endHour; hour++) {
      for (let minute = 0; minute <= 30; minute += schedule.slotInterval) {
        candidateMinutes.push(hour * 60 + minute);
      }
    }
  }

  const restrictToVariant =
    restrictChristmasVariant && schedule.kind === 'christmas' && CHRISTMAS_TOUR_IDS.includes(currentTourId);

  // A long ride must not be offered at a time it could not finish. Without this
  // every tour was bookable to 23:30 regardless of length, so a 125-minute ride
  // would return at 01:35 and write a driver-calendar entry to match.
  const lastDeparture = lastDepartureMinutes(duration);
  const earliestDeparture = schedule.earliestMinutes ?? -1;

  const slots = [];
  for (const totalMinutes of candidateMinutes) {
    if (totalMinutes < earliestDeparture) continue;
    if (totalMinutes > lastDeparture) continue;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;

    // Past cutoff only applies when booking today.
    if (isToday && totalMinutes <= cutoffMinutes) continue;

    // Drop departures this particular Christmas variant does not run.
    if (restrictToVariant && !variantsAtChristmasTime(totalMinutes, dateString).includes(currentTourId)) {
      continue;
    }

    if (isSlotBlocked(blockedTimes, { slotMinutes: totalMinutes, dateString, tourId: currentTourId })) {
      continue;
    }

    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const timeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;

    slots.push({ time: timeString, value: timeString, hour, minute });
  }

  return slots;
}
